import type { RideRecord } from "../types/ride";

export type PowerDurationPoint = {
  durationSeconds: number;
  bestAveragePower: number | null;
  startIndex: number | null;
  endIndex: number | null;
};

export type ValidPowerSample = {
  index: number;
  powerWatts: number;
};

export const DEFAULT_POWER_DURATION_SECONDS = [
  1,
  5,
  10,
  15,
  30,
  60,
  120,
  180,
  300,
  480,
  600,
  720,
  1200,
  1800,
  2700,
  3600,
  5400,
  7200,
  10800
];

export function getValidPowerSamples(records: RideRecord[]): ValidPowerSample[] {
  return records
    .map((record, index) => ({ index, powerWatts: record.powerWatts }))
    .filter(
      (sample): sample is ValidPowerSample =>
        typeof sample.powerWatts === "number" && Number.isFinite(sample.powerWatts) && sample.powerWatts >= 0
    );
}

export function computeMaximumInstantaneousPower(records: RideRecord[]): number | null {
  const validSamples = getValidPowerSamples(records);
  if (validSamples.length === 0) {
    return null;
  }

  return Math.max(...validSamples.map(sample => sample.powerWatts));
}

function getMedian(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 1) {
    return sorted[middle];
  }

  return (sorted[middle - 1] + sorted[middle]) / 2;
}

function getRecordTimeSeconds(record: RideRecord): number | null {
  if (record.elapsedSeconds !== null && record.elapsedSeconds !== undefined && Number.isFinite(record.elapsedSeconds)) {
    return record.elapsedSeconds;
  }

  if (record.timestamp) {
    const timestampMs = Date.parse(record.timestamp);
    if (!Number.isNaN(timestampMs)) {
      return timestampMs / 1000;
    }
  }

  return null;
}

type TimedRecord = {
  index: number;
  timeSeconds: number;
  powerWatts: number | null;
};

type PowerTimeline = {
  startTimeSeconds: number;
  totalDurationSeconds: number;
  medianIntervalSeconds: number;
  energyBySecond: number[];
  timedRecords: TimedRecord[];
};

function buildPowerTimeline(records: RideRecord[]): PowerTimeline | null {
  const timedRecords: TimedRecord[] = records
    .map((record, index) => {
      const timeSeconds = getRecordTimeSeconds(record);
      return {
        index,
        timeSeconds,
        powerWatts:
          record.powerWatts !== null && record.powerWatts !== undefined && Number.isFinite(record.powerWatts)
            ? record.powerWatts
            : null
      };
    })
    .filter(record => record.timeSeconds !== null)
    .map(record => ({ ...record, timeSeconds: record.timeSeconds as number }));

  if (timedRecords.length < 2) {
    return null;
  }

  timedRecords.sort((left, right) => left.timeSeconds - right.timeSeconds);

  const positiveIntervals: number[] = [];
  for (let index = 1; index < timedRecords.length; index += 1) {
    const intervalSeconds = timedRecords[index].timeSeconds - timedRecords[index - 1].timeSeconds;
    if (intervalSeconds > 0) {
      positiveIntervals.push(intervalSeconds);
    }
  }

  const medianIntervalSeconds = getMedian(positiveIntervals);
  if (medianIntervalSeconds === null || medianIntervalSeconds <= 0) {
    return null;
  }

  const firstTimestampSeconds = timedRecords[0].timeSeconds;
  const lastTimestampSeconds = timedRecords[timedRecords.length - 1].timeSeconds;
  const totalDurationSeconds = (lastTimestampSeconds - firstTimestampSeconds) + medianIntervalSeconds;
  if (totalDurationSeconds <= 0) {
    return null;
  }

  const totalTimelineSeconds = Math.max(1, Math.ceil(totalDurationSeconds));
  const energyBySecond = new Array<number>(totalTimelineSeconds).fill(0);
  const longGapThresholdSeconds = medianIntervalSeconds * 5;

  for (let index = 0; index < timedRecords.length; index += 1) {
    const current = timedRecords[index];
    const next = timedRecords[index + 1];

    const segmentStartSeconds = current.timeSeconds - firstTimestampSeconds;
    const segmentEndSeconds = next
      ? next.timeSeconds - firstTimestampSeconds
      : segmentStartSeconds + medianIntervalSeconds;

    if (segmentEndSeconds <= segmentStartSeconds) {
      continue;
    }

    const segmentDurationSeconds = segmentEndSeconds - segmentStartSeconds;
    let segmentPowerWatts = current.powerWatts ?? 0;

    if (next && segmentDurationSeconds > longGapThresholdSeconds) {
      segmentPowerWatts = 0;
    }

    const startSecond = Math.floor(segmentStartSeconds);
    const endSecond = Math.ceil(segmentEndSeconds) - 1;

    for (let second = startSecond; second <= endSecond; second += 1) {
      if (second < 0 || second >= energyBySecond.length) {
        continue;
      }

      const overlapStartSeconds = Math.max(segmentStartSeconds, second);
      const overlapEndSeconds = Math.min(segmentEndSeconds, second + 1);
      const overlapDurationSeconds = overlapEndSeconds - overlapStartSeconds;

      if (overlapDurationSeconds > 0) {
        energyBySecond[second] += segmentPowerWatts * overlapDurationSeconds;
      }
    }
  }

  return {
    startTimeSeconds: firstTimestampSeconds,
    totalDurationSeconds,
    medianIntervalSeconds,
    energyBySecond,
    timedRecords
  };
}

export function validatePowerDurationRequest(records: RideRecord[], durationSeconds: number): string | null {
  const timeline = buildPowerTimeline(records);
  if (timeline === null) {
    return "Ride contains no usable time-series data.";
  }

  if (timeline.totalDurationSeconds < durationSeconds) {
    return `Ride duration ${timeline.totalDurationSeconds.toFixed(0)}s is shorter than the requested ${durationSeconds}s window.`;
  }

  return null;
}

export function computePowerDurationCurve(records: RideRecord[], durations: number[] = DEFAULT_POWER_DURATION_SECONDS): PowerDurationPoint[] {
  const timeline = buildPowerTimeline(records);
  if (timeline === null) {
    return durations.map(durationSeconds => ({
      durationSeconds,
      bestAveragePower: null,
      startIndex: null,
      endIndex: null
    }));
  }

  const prefixEnergy = new Array<number>(timeline.energyBySecond.length + 1).fill(0);
  for (let index = 0; index < timeline.energyBySecond.length; index += 1) {
    prefixEnergy[index + 1] = prefixEnergy[index] + timeline.energyBySecond[index];
  }

  const findStartIndex = (windowStartSeconds: number): number | null => {
    const absoluteWindowStartSeconds = timeline.startTimeSeconds + windowStartSeconds;
    for (const record of timeline.timedRecords) {
      if (record.timeSeconds >= absoluteWindowStartSeconds) {
        return record.index;
      }
    }

    return null;
  };

  const findEndIndex = (windowEndSeconds: number): number | null => {
    const absoluteWindowEndSeconds = timeline.startTimeSeconds + windowEndSeconds;
    let candidate: number | null = null;

    for (const record of timeline.timedRecords) {
      if (record.timeSeconds <= absoluteWindowEndSeconds) {
        candidate = record.index;
      } else {
        break;
      }
    }

    return candidate;
  };

  return durations.map(durationSeconds => {
    if (durationSeconds === 1) {
      const validSamples = getValidPowerSamples(records);
      if (validSamples.length === 0) {
        return {
          durationSeconds,
          bestAveragePower: null,
          startIndex: null,
          endIndex: null
        };
      }

      let bestSample = validSamples[0];
      for (let index = 1; index < validSamples.length; index += 1) {
        if (validSamples[index].powerWatts > bestSample.powerWatts) {
          bestSample = validSamples[index];
        }
      }

      return {
        durationSeconds,
        bestAveragePower: bestSample.powerWatts,
        startIndex: bestSample.index,
        endIndex: bestSample.index
      };
    }

    if (timeline.totalDurationSeconds < durationSeconds) {
      return {
        durationSeconds,
        bestAveragePower: null,
        startIndex: null,
        endIndex: null
      };
    }

    let bestAveragePower: number | null = null;
    let bestStartIndex: number | null = null;
    let bestEndIndex: number | null = null;

    const maxWindowStartSecond = Math.max(0, Math.floor(timeline.totalDurationSeconds - durationSeconds));
    for (let windowStartSecond = 0; windowStartSecond <= maxWindowStartSecond; windowStartSecond += 1) {
      const windowEndSecond = windowStartSecond + durationSeconds;
      const windowEnergy = prefixEnergy[windowEndSecond] - prefixEnergy[windowStartSecond];
      const averagePower = windowEnergy / durationSeconds;

      if (bestAveragePower === null || averagePower > bestAveragePower) {
        bestAveragePower = averagePower;
        bestStartIndex = findStartIndex(windowStartSecond);
        bestEndIndex = findEndIndex(windowEndSecond);
      }
    }

    return {
      durationSeconds,
      bestAveragePower,
      startIndex: bestStartIndex,
      endIndex: bestEndIndex
    };
  });
}
